import {HttpContext, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {IApi} from '@entities/api';
import {
  ESubjectType,
  IListSubject,
  ISubject,
  ISubjectFavoritesStatus,
  ISubjectRelatedLink,
  ISyncStatusProgress,
} from '@entities/subject';
import {IActivity} from '@shared/entities/activity/activity.interface';
import {IBranch} from '@shared/entities/branches/branches.interfaces';
import {
  IEnforcementsApiResponse,
  IGetEnforcementsPayload,
} from '@shared/entities/enforcement/enforcement.interface';
import {
  IBeneficiary,
  IFounder,
} from '@shared/entities/participants/participants.interfaces';
import {SSR_TRANSFER} from '@shared/interceptors/ssr-transfer.interceptor';
import {ApiService} from '@shared/services';
import {Observable} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SubjectApi extends ApiService {
  public getSubject(
    subjectId: string | number,
    type: ESubjectType
  ): Observable<IApi<ISubject>> {
    const options = {
      context: new HttpContext().set(SSR_TRANSFER, true),
    };

    if (type === ESubjectType.Physical) {
      return this.get<IApi<ISubject>>(
        `${this.apiUrl}/subjects/${subjectId}`,
        options
      );
    } else {
      return this.get<IApi<ISubject>>(
        `${this.apiUrl}/subjects/org/${subjectId}`,
        options
      );
    }
  }

  public getLinkedSubjects(
    subjectId: number
  ): Observable<IApi<ISubjectRelatedLink[]>> {
    return this.get<IApi<ISubjectRelatedLink[]>>(
      `${this.apiUrl}/subjects/search/related-links/${subjectId}`
    );
  }

  public getRelatedLinks(
    subjectId: number
  ): Observable<IApi<ISubjectRelatedLink[]>> {
    return this.get<IApi<ISubjectRelatedLink[]>>(
      `${this.apiUrl}/subjects/${subjectId}/related-links`
    );
  }

  public getSubjectFavorites(
    subjectId: number
  ): Observable<ISubjectFavoritesStatus> {
    return this.get<ISubjectFavoritesStatus>(
      `${this.apiUrl}/favorite/${subjectId}`
    );
  }

  public markFavorite(subjectId: number): Observable<IApi<IListSubject>> {
    return this.post<IApi<IListSubject>>(`${this.apiUrl}/favorite`, {
      subjectId,
    });
  }

  public unmarkFavorite(subjectId: number): Observable<IApi<IListSubject>> {
    return this.delete<IApi<IListSubject>>(`${this.apiUrl}/favorite`, null, {
      params: {subjectId},
    });
  }

  public startSync(subjectId: number): Observable<IApi<ISyncStatusProgress>> {
    return this.post<IApi<ISyncStatusProgress>>(
      `${this.apiUrl}/subjects/sync`,
      {
        subjectId,
      }
    );
  }

  public checkSync(subjectId: number): Observable<IApi<ISyncStatusProgress>> {
    return this.get<IApi<ISyncStatusProgress>>(
      `${this.apiUrl}/subjects/sync/${subjectId}`
    );
  }

  public getBeneficiaries(subjectId: number): Observable<IApi<IBeneficiary[]>> {
    return this.get<IApi<IBeneficiary[]>>(
      `${this.apiUrl}/subjects/${subjectId}/beneficiaries`
    );
  }

  public getFounders(subjectId: number): Observable<IApi<IFounder[]>> {
    return this.get<IApi<IFounder[]>>(
      `${this.apiUrl}/subjects/${subjectId}/founders`
    );
  }

  public getBranches(subjectId: number): Observable<IApi<IBranch[]>> {
    return this.get<IApi<IBranch[]>>(
      `${this.apiUrl}/subjects/${subjectId}/branches`
    );
  }

  public getEnforcements({
    subjectId,
    sort,
    page,
    perPage,
    debtorBirthdate,
  }: IGetEnforcementsPayload): Observable<IEnforcementsApiResponse> {
    let params = new HttpParams()
      .set('sortBy', sort.sortBy)
      .set('sortDirection', sort.sortOrder)
      .set('page', page)
      .set('perPage', perPage);
    if (debtorBirthdate) {
      params = params.set('debtorBirthdate', debtorBirthdate);
    }
    return this.get<IEnforcementsApiResponse>(
      `${this.apiUrl}/subjects/${subjectId}/enforcements`,
      {params}
    );
  }

  public getActivities(subjectId: number): Observable<IApi<IActivity[]>> {
    return this.get<IApi<IActivity[]>>(
      `${this.apiUrl}/subjects/${subjectId}/activities`
    );
  }
}
